import React from 'react';
import styled from 'styled-components';
import CZTooltip, { QuestionToolTip } from '../CZTooltip/CZTooltip';
import { GroupedOptions, Option } from '../inputs/base/DropDown';
import {
    CurrencyInput,
    FormCheckbox,
    FormDatePicker,
    FormDropDown,
    FormNumberInput,
    FormTextArea,
    FormTextInput,
    NumberUnitInput,
    FormAutoComplete
} from '../inputs/form';

export type editTypes = 'dropdown' | 'date' | 'currency' | 'unit' | 'checkbox' | 'number' | 'textarea' | 'autocomplete';

export type editableDetail = {
    value?: any;
    type?: editTypes;
    options?: Option[];
    groupedOptions?: GroupedOptions;
    placeholder?: string;
    hideTime?: boolean;
    min?: number;
    max?: number;
    step?: number;
    dropDownMode?: 'multiple' | 'tags';
    autoSize?:
        | boolean
        | {
              minRows?: number;
              maxRows?: number;
          };
    width?: string;
    dropDownOptionRenderer?: (option: Option & { color?: string }) => Promise<JSX.Element> | JSX.Element;
    /** Sanitize input value (e.g. to restrict special characters) */
    sanitize?: (value: string) => string;
};
export type detail<T = any> = {
    name: Extract<keyof T, string>;
    showCondition: boolean;
    editable: false | JSX.Element | editableDetail;
    content?: JSX.Element;
    title: string | JSX.Element;
    value?: string | JSX.Element;
    tooltip?: string;
    hideTitle?: boolean;
};

const renderEditable = (name: string, editable: editableDetail, values: any): JSX.Element => {
    const { type, options, groupedOptions, placeholder, hideTime, min, max, step, dropDownMode, dropDownOptionRenderer, autoSize, width, sanitize } = editable;
    switch (type) {
        case 'dropdown':
            return (
                <FormDropDown
                    name={name}
                    style={{ width: '100%' }}
                    placeHolder={placeholder}
                    options={groupedOptions || options || []}
                    showSearch={true}
                    mode={dropDownMode}
                    optionRenderer={dropDownOptionRenderer}
                />
            );
        case 'date':
            return <FormDatePicker name={name} style={{ width: '100%' }} placeHolder={placeholder} showTime={!hideTime} />;
        case 'number':
            return <FormNumberInput name={name} placeHolder={placeholder} style={{ width: '100%' }} min={min} max={max} step={step} />;
        case 'currency':
            return (
                <CurrencyInput
                    currencyValue={values[`${name}Currency`] ?? 'USD'}
                    numberInputName={name}
                    numberPlaceholder={placeholder}
                    unitInputName={`${name}Currency`}
                />
            );
        case 'unit':
            return (
                <NumberUnitInput
                    numberInputName={name}
                    numberInputWidth="auto"
                    min={0}
                    step={0.001}
                    unitInputName={`${name}Unit`}
                    numberPlaceholder={placeholder}
                    unitOptions={options || []}
                />
            );
        case 'checkbox':
            return (
                <FormCheckbox name={name}>
                    <span style={{ color: '#008ac9' }}>{placeholder}</span>
                </FormCheckbox>
            );

        case 'textarea':
            return <FormTextArea name={name} autoSize={autoSize} placeHolder={placeholder} style={{ width: width ?? '100%' }} />;

        case 'autocomplete':
            return <FormAutoComplete name={name} style={{ width: '100%' }} placeHolder={placeholder} options={options || []} showSearch={true} />;

        default:
            return <FormTextInput name={name} placeHolder={placeholder} style={{ width: '100%' }} sanitize={sanitize} />;
    }
};

function renderDetail<T = any>(detail: detail<T>, key: number, values: any, detailsEdit: boolean) {
    const { name, showCondition, content, title, value, editable, tooltip, hideTitle } = detail;

    return (
        showCondition &&
        (editable || !detailsEdit || (editable === false && (value || content))) && (
            <DetailBox key={key}>
                {!hideTitle && (
                    <div style={{ display: 'flex', alignItems: 'center', height: '22px' }}>
                        <DetailTitle>{title}</DetailTitle>
                        {!!tooltip && <CZTooltip text={tooltip}>{QuestionToolTip}</CZTooltip>}
                    </div>
                )}
                {!!content && !detailsEdit ? (
                    content
                ) : editable && detailsEdit ? (
                    React.isValidElement(editable) ? (
                        editable
                    ) : (
                        renderEditable(name, editable as editableDetail, values)
                    )
                ) : (
                    <DetailValue>{value || '-'}</DetailValue>
                )}
            </DetailBox>
        )
    );
}

type Props<T = any> = {
    values: T;
    detailsEdit: boolean;
    detailsData: Array<Array<detail<T>>>;
};

function EntityDetails<T = any>({ detailsData, detailsEdit, values }: Props<T>) {
    return (
        <DetailsGrid data-editing={detailsEdit}>
            {detailsData.map((row: Array<detail<T>>, i: number) => {
                const visibleDetails = row.filter(
                    (d) => d.showCondition && (!!d.editable || !detailsEdit || (d.editable === false && (d.value || d.content)))
                );
                if (visibleDetails.length === 0) return <div key={i} />;
                return (
                    <DetailsRow key={i}>
                        {row.map((detail: detail<T>, j: number) => {
                            return renderDetail<T>(detail, j, values, detailsEdit);
                        })}
                    </DetailsRow>
                );
            })}
        </DetailsGrid>
    );
}

const DetailsGrid = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    gap: 20px 0;

    /* Tighter spacing in read-only mode */
    &[data-editing='false'] {
        gap: 10px 0;
    }
    &[data-editing='false'] [data-detail-row] {
        gap: 8px 14px;
    }
    &[data-editing='false'] [data-detail-box] {
        gap: 2px;
    }
`;

const DetailsRow = styled.div.attrs({ 'data-detail-row': true })`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(200px, 100%), 1fr));
    gap: 16px 24px;
    width: 100%;
    max-width: 100%;
    align-items: start;

    @media screen and (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 16px;
    }
`;

const DetailBox = styled.div.attrs({ 'data-detail-box': true })`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const DetailTitle = styled.div`
    color: #008ac9;
    display: flex;
    align-items: center;
    font-size: 13px;
    line-height: 20px;
    flex-shrink: 0;
`;

const DetailValue = styled.div`
    color: #394372;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
    min-height: 22px;
`;

export default EntityDetails;
